import React, { useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import {
  Hub as NetworkIcon,
  Chat as ChatIcon,
  Message as MessageIcon,
  MarkEmailUnread as UnreadIcon,
  Handshake as HandshakeIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { NetworkingMetrics, CHART_COLORS } from '../../types/dashboard.types';
import MetricCard from './MetricCard';
import ChartCard from './ChartCard';
import SectionHeader from './SectionHeader';
import EmptyState from './EmptyState';

export interface NetworkingMetricsSectionProps {
  data: NetworkingMetrics;
  loading?: boolean;
}

const NetworkingMetricsSection: React.FC<NetworkingMetricsSectionProps> = ({
  data,
  loading = false,
}) => {
  const connectionData = useMemo(
    () =>
      data.connectionsByStatus.map((item, index) => ({
        name: item.status.charAt(0).toUpperCase() + item.status.slice(1),
        value: item.count,
        color: CHART_COLORS.palette[index % CHART_COLORS.palette.length],
      })),
    [data.connectionsByStatus]
  );

  const totalConnections = connectionData.reduce((sum, item) => sum + item.value, 0);
  const hasConnectionData = connectionData.length > 0;

  return (
    <Box>
      <SectionHeader title="Networking & Engagement" icon={<NetworkIcon />} />

      <Grid container spacing={3}>
        {/* Metric Cards Row */}
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Acceptance Rate"
            value={data.acceptanceRate}
            icon={<HandshakeIcon />}
            color={CHART_COLORS.success}
            format="percent"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Total Connections"
            value={totalConnections}
            icon={<NetworkIcon />}
            color={CHART_COLORS.primary}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Conversations"
            value={data.totalConversations}
            icon={<ChatIcon />}
            color={CHART_COLORS.purple}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Total Messages"
            value={data.totalMessages}
            icon={<MessageIcon />}
            color={CHART_COLORS.cyan}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Unread Messages"
            value={data.unreadMessages}
            icon={<UnreadIcon />}
            color={CHART_COLORS.warning}
            loading={loading}
          />
        </Grid>

        {/* Chart */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Connections by Status"
            subtitle="Distribution of connection statuses"
            height={280}
            loading={loading}
          >
            {!hasConnectionData ? (
              <EmptyState
                message="No connection data available"
                icon={<NetworkIcon sx={{ fontSize: 32, color: '#9ca3af' }} />}
                height={250}
              />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={connectionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {connectionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  {connectionData.map((item, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: item.color,
                        }}
                      />
                      <Box sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {item.name}: {item.value}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </ChartCard>
        </Grid>

        {/* Summary Card */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Engagement Summary"
            subtitle="Overview of networking activity"
            height={280}
            loading={loading}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  bgcolor: '#f3f4f6',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HandshakeIcon sx={{ color: CHART_COLORS.success }} />
                  <Box sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    Connection Acceptance
                  </Box>
                </Box>
                <Box
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: CHART_COLORS.success,
                  }}
                >
                  {data.acceptanceRate}%
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  bgcolor: '#f3f4f6',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChatIcon sx={{ color: CHART_COLORS.purple }} />
                  <Box sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    Avg Messages per Conversation
                  </Box>
                </Box>
                <Box
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: CHART_COLORS.purple,
                  }}
                >
                  {data.totalConversations > 0
                    ? (data.totalMessages / data.totalConversations).toFixed(1)
                    : '0'}
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  bgcolor: data.unreadMessages > 0 ? '#fef3c7' : '#f3f4f6',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <UnreadIcon
                    sx={{
                      color:
                        data.unreadMessages > 0
                          ? CHART_COLORS.warning
                          : '#6b7280',
                    }}
                  />
                  <Box sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    Pending Messages
                  </Box>
                </Box>
                <Box
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color:
                      data.unreadMessages > 0 ? CHART_COLORS.warning : '#6b7280',
                  }}
                >
                  {data.unreadMessages}
                </Box>
              </Box>
            </Box>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NetworkingMetricsSection;
